import { useEffect, useState } from "react";
import { useDogs } from "@/context/DogsContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/app/Bits";
import EmptyDogState from "@/components/app/EmptyDogState";
import TitlesSection from "@/components/app/TitlesSection";
import { Trash2, Trophy } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

export default function ShowResultsPage() {
  const { activeDog } = useDogs();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ show_date: format(new Date(), "yyyy-MM-dd"), show_name: "", judge: "", placement: "", points: "", title_earned: false, notes: "" });

  const load = async () => {
    if (!activeDog) return;
    const { data } = await api.get("/show-results", { params: { dog_id: activeDog.id } });
    setItems(data.items || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeDog]);

  if (!activeDog) return <EmptyDogState>{null}</EmptyDogState>;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.show_name) return;
    try {
      await api.post("/show-results", { dog_id: activeDog.id, ...form });
      toast.success("Result added");
      setForm({ show_date: format(new Date(), "yyyy-MM-dd"), show_name: "", judge: "", placement: "", points: "", title_earned: false, notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this result?")) return;
    await api.delete(`/show-results/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="show-results-page">
      <SectionTitle overline="History" title="Show results" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border shadow-none rounded-lg">
          <CardContent className="p-0">
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground p-10 text-center">No results yet — add your first one →</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-6 py-3">Date</th>
                    <th className="px-6 py-3">Show</th>
                    <th className="px-6 py-3">Placement</th>
                    <th className="px-6 py-3">Points</th>
                    <th className="px-6 py-3 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} className="border-t border-border" data-testid={`result-row-${r.id}`}>
                      <td className="px-6 py-3 whitespace-nowrap">{format(parseISO(r.show_date), "MMM d, yyyy")}</td>
                      <td className="px-6 py-3">
                        <p className="font-medium">{r.show_name}</p>
                        {r.judge ? <p className="text-xs text-muted-foreground">Judge: {r.judge}</p> : null}
                        {r.notes ? <p className="text-xs text-foreground/80 mt-1">{r.notes}</p> : null}
                      </td>
                      <td className="px-6 py-3">
                        {r.placement ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-accent text-accent-foreground rounded px-2 py-0.5">
                            <Trophy size={11} /> {r.placement}
                          </span>
                        ) : "—"}
                        {r.title_earned ? (
                          <span className="ml-1.5 inline-flex items-center text-[10px] uppercase tracking-wider bg-primary text-primary-foreground rounded px-1.5 py-0.5">title</span>
                        ) : null}
                      </td>
                      <td className="px-6 py-3">{r.points || "—"}</td>
                      <td className="px-6 py-3 text-right">
                        <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive">
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card className="border-border shadow-none rounded-lg">
          <CardContent className="p-6">
            <p className="font-medium mb-4">Add show result</p>
            <form onSubmit={submit} className="space-y-3" data-testid="add-result-form">
              <div>
                <Label>Show date</Label>
                <Input type="date" value={form.show_date} onChange={(e) => setForm({ ...form, show_date: e.target.value })} className="mt-1.5" required />
              </div>
              <div>
                <Label>Show name</Label>
                <Input value={form.show_name} onChange={(e) => setForm({ ...form, show_name: e.target.value })} required className="mt-1.5" data-testid="result-show-name-input" />
              </div>
              <div>
                <Label>Judge</Label>
                <Input value={form.judge} onChange={(e) => setForm({ ...form, judge: e.target.value })} className="mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Placement</Label>
                  <Input value={form.placement} onChange={(e) => setForm({ ...form, placement: e.target.value })} className="mt-1.5" placeholder="BOB, WD, RWB…" />
                </div>
                <div>
                  <Label>Points</Label>
                  <Input value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="result-title-checkbox">
                <input
                  type="checkbox"
                  checked={!!form.title_earned}
                  onChange={(e) => setForm({ ...form, title_earned: e.target.checked })}
                  className="h-4 w-4 accent-primary"
                />
                New title earned at this show
              </label>
              <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="submit-result-button">Add result</Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Titles section — separate collection, sits underneath Show results.
          Same active dog drives both. */}
      <TitlesSection dog={activeDog} />
    </div>
  );
}
